import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, Edit, MoreHorizontal, Image, Package, Wand2, Filter, SortAsc, SortDesc, RefreshCw } from "lucide-react";
import { getAuthData, getStoreId } from "../utils/auth";
import * as logger from "../utils/logger";
import { showToast } from "../utils/toast";

const toastOpts = (ref) => ({ anchor: ref, anchorPosition: "above" });

const Products = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [generatingAltText, setGeneratingAltText] = useState({});
  /** `all` | `missing_alt` | `with_alt` — server-side filter (same idea as Wix dashboard alt status) */
  const [imagesAltFilter, setImagesAltFilter] = useState("all");
  /** `all` | `online` — Ecwid products with enabled=true (storefront-visible) */
  const [productListingFilter, setProductListingFilter] = useState("all");
  const [altFilterScanCapped, setAltFilterScanCapped] = useState(false);

  const fetchProductsInFlightRef = useRef(false);
  const fetchCategoriesInFlightRef = useRef(false);
  const bulkAltButtonRef = useRef(null);
  const generateAltButtonRef = useRef(null);

  const itemsPerPageOptions = [10, 20, 50, 100];

  useEffect(() => {
    if (fetchProductsInFlightRef.current) return;
    fetchProductsInFlightRef.current = true;
    fetchProducts().finally(() => { fetchProductsInFlightRef.current = false; });
  }, [currentPage, itemsPerPage, selectedCategory, searchTerm, sortField, sortDirection, imagesAltFilter, productListingFilter]);

  useEffect(() => {
    if (fetchCategoriesInFlightRef.current) return;
    fetchCategoriesInFlightRef.current = true;
    fetchCategories().finally(() => { fetchCategoriesInFlightRef.current = false; });
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId !== null) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openDropdownId]);

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const authData = getAuthData();

      if (!authData?.store_id || !authData?.access_token) {
        logger.error("Not authenticated");
        return;
      }

      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const allCategories = [];
      let offset = 0;
      const limit = 100; // Fetch in batches of 100
      let total = null;
      let hasMore = true;

      // Fetch all categories in batches
      while (hasMore) {
        const response = await fetch(
          `${apiUrl}/api/categories?storeId=${authData.store_id}&token=${authData.access_token}&limit=${limit}&offset=${offset}`
        );
        const data = await response.json();

        if (response.ok) {
          // Get total count from first response
          if (total === null) {
            total = data.total || 0;
          }
          
          if (data.items && data.items.length > 0) {
            allCategories.push(...data.items);
            offset += limit;
            // Continue if we got a full batch and haven't reached the total
            hasMore = data.items.length === limit && allCategories.length < total;
          } else {
            hasMore = false;
          }
        } else {
          logger.error("Failed to fetch categories:", data.error);
          hasMore = false;
        }
      }

      setCategories(allCategories);
    } catch (error) {
      logger.error("Failed to fetch categories:", error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setAltFilterScanCapped(false);
      const authData = getAuthData();
      const storeId = getStoreId();

      if (!authData?.store_id || !authData?.access_token) {
        logger.error("Not authenticated");
        setLoading(false);
        return;
      }

      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const settingsResponse = await fetch(`${apiUrl}/api/settings?id=${storeId}`);
      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        if (settingsData.success && settingsData.settings) {
          setSettings(settingsData.settings);
        }
      }

      const offset = (currentPage - 1) * itemsPerPage;
      let url = `${apiUrl}/api/products?storeId=${authData.store_id}&token=${authData.access_token}&limit=${itemsPerPage}&offset=${offset}`;

      // Add category filter if specified
      if (selectedCategory) {
        url += `&category=${selectedCategory}`;
      }

      // Add search keyword if specified
      if (searchTerm) {
        url += `&keyword=${encodeURIComponent(searchTerm)}`;
      }

      // Add sorting if specified
      if (sortField) {
        url += `&sort=${sortField}&direction=${sortDirection}`;
      }

      if (imagesAltFilter === "missing_alt" || imagesAltFilter === "with_alt") {
        url += `&imagesAltFilter=${encodeURIComponent(imagesAltFilter)}`;
      }

      if (productListingFilter === "online") {
        url += "&enabled=true";
      }

      const response = await fetch(url);
      const data = await response.json();

      if (response.ok) {
        setProducts(data.items || []);
        setTotalProducts(data.total || 0);
        setAltFilterScanCapped(data.altFilterScanCapped === true);
      } else {
        logger.error("Failed to fetch products:", data.error);
      }
    } catch (error) {
      logger.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  // Products are now sorted server-side via API, so we use them directly
  const sortedProducts = products;

  const onItemsPerPageChange = (newRange) => {
    setCurrentPage(1);
    setItemsPerPage(newRange);
  };

  const onSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sort changes
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setSelectedCategory("");
    setSearchTerm("");
    setSortField("");
    setSortDirection("asc");
    setImagesAltFilter("all");
    setProductListingFilter("all");
    setCurrentPage(1);
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      }
      return [...prev, productId];
    });
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === sortedProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(sortedProducts.map(product => product.id));
    }
  };

  const handleBulkAltText = async () => {
    if (selectedProducts.length > 0) {
      // Check if credits are available
      // if (!settings || settings.credits_available <= 0) {
      //   showToast(t("products.outOfCredits"), "error", 8000);
      //   return;
      // }

      try {
        const authData = getAuthData();
        const storeId = getStoreId();

        if (!authData?.store_id || !authData?.access_token) {
          logger.error("Not authenticated");
          return;
        }

        const selectedProductObjects = sortedProducts.filter(product =>
          selectedProducts.includes(product.id)
        ).map(product => ({
          id: product.id,
          name: product.name,
          brand_id: product.brandId || 0
        }));

        logger.log("selectedProductObjects", selectedProductObjects);

        const apiUrl = import.meta.env.VITE_BACKEND_URL;
        const response = await fetch(`${apiUrl}/api/bulkproduct`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            storeId: storeId,
            selectedProductObjects: selectedProductObjects
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || t("products.failedToGenerateAltText"));
        }

        showToast(t("products.bulkStarted", { count: selectedProducts.length }), "success", 3000, toastOpts(bulkAltButtonRef));
        setSelectedProducts([]);
      } catch (error) {
        logger.error("Failed to generate bulk alt text:", error);
        showToast(error.message || t("products.failedToGenerateAltText"), "error", 3000, toastOpts(bulkAltButtonRef));
      }
    }
  };

  const handleGenerateAltText = async (productId, productName, event) => {
    if (event?.currentTarget) generateAltButtonRef.current = event.currentTarget;
    // Check if credits are available

    setGeneratingAltText(prev => ({ ...prev, [productId]: true }));

    try {
      const authData = getAuthData();
      const storeId = getStoreId();

      if (!authData?.store_id || !authData?.access_token) {
        logger.error("Not authenticated");
        return;
      }

      const selectedProductObjects = [{
        id: productId,
        name: productName,
        brand_id: 0 // Default brand ID for Ecwid
      }];

      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/bulkproduct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          storeId: storeId,
          selectedProductObjects: selectedProductObjects
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.error === "quota_exceeded") {
          showToast(t("products.quotaExceeded"), "error", 6000, toastOpts(generateAltButtonRef));
          return;
        } else {
          throw new Error(data.error || t("products.failedToGenerateAltText"));
        }
      }

      showToast(t("products.altTextStartedForProduct", { name: productName }), "success", 3000, toastOpts(generateAltButtonRef));
      setOpenDropdownId(null);
    } catch (error) {
      logger.error("Failed to generate alt text:", error);
      showToast(error.message || t("products.failedToGenerateAltText"), "error", 3000, toastOpts(generateAltButtonRef));
    } finally {
      setGeneratingAltText(prev => ({ ...prev, [productId]: false }));
    }
  };



  const toggleDropdown = (id, event) => {
    event.stopPropagation();
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const handleEditProduct = (productId) => {
    // Open Ecwid product editor
    const authData = getAuthData();
    if (authData?.store_id) {
      const productUrl = `https://my.ecwid.com/store/${authData.store_id}#product:mode=edit&id=${productId}&return=products`;
      window.open(productUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <div className="loading-dots mb-3" role="status" style={{ width: '24px', height: '24px', margin: '0 auto' }}>
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
          <p className="text-muted" style={{ fontSize: '0.875rem' }}>{t('products.loadingProducts')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-1">
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h3 className="mb-4 fw-bold" style={{ fontSize: '1.25rem' }}>{t('products.myProductList')}</h3>
              <p className="text-muted mb-4" style={{ fontSize: '0.875rem' }}>
                {t('products.intro')}
              </p>

              {/* Filters - same style as SEO Optimizer */}
              <div className="card mb-3 border-0 shadow-sm">
                <div className="card-body py-2 px-3">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="d-flex align-items-center gap-1 text-muted small fw-semibold me-1">
                      <Filter size={14} strokeWidth={2.5} />
                      Filters
                    </span>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: "auto", minWidth: "220px" }}
                      value={imagesAltFilter}
                      onChange={(e) => {
                        setImagesAltFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      aria-label={t("products.imagesAltFilterAria")}
                    >
                      <option value="all">{t("products.imagesAltFilterAll")}</option>
                      <option value="with_alt">{t("products.imagesAltFilterWithAlt")}</option>
                      <option value="missing_alt">{t("products.imagesAltFilterMissing")}</option>
                    </select>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: "auto", minWidth: "140px" }}
                      value={selectedCategory}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      disabled={categoriesLoading}
                    >
                      <option value="">All Categories</option>
                      {categoriesLoading ? (
                        <option disabled>Loading categories...</option>
                      ) : (
                        categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name} ({category.productCount || 0})
                          </option>
                        ))
                      )}
                    </select>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: "auto", minWidth: "110px" }}
                      value={productListingFilter}
                      onChange={(e) => {
                        setProductListingFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      aria-label={t("seoOptimizer.productListingFilter")}
                    >
                      <option value="all">{t("seoOptimizer.listingAll")}</option>
                      <option value="online">{t("seoOptimizer.listingOnlineOnly")}</option>
                    </select>
                    {/* <select
                      className="form-select form-select-sm"
                      style={{ width: "auto", minWidth: "140px" }}
                      value={sortField}
                      onChange={(e) => onSort(e.target.value)}
                    >
                      <option value="">Sort by...</option>
                      <option value="name">Name</option>
                      <option value="price">Price</option>
                      <option value="created">Date Created</option>
                      <option value="updated">Last Updated</option>
                    </select>
                    <div className="d-flex gap-1">
                      <button
                        type="button"
                        className={`btn btn-sm ${sortDirection === 'asc' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setSortDirection('asc')}
                        disabled={!sortField}
                        title="Ascending"
                      >
                        <SortAsc size={14} />
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${sortDirection === 'desc' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setSortDirection('desc')}
                        disabled={!sortField}
                        title="Descending"
                      >
                        <SortDesc size={14} />
                      </button>
                    </div> */}
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ width: "clamp(120px, 18vw, 200px)" }}
                      placeholder="Search by name..."
                      value={searchTerm}
                      onChange={handleSearch}
                    />
                    {(selectedCategory || searchTerm || sortField || imagesAltFilter !== "all" || productListingFilter !== "all") && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={clearFilters}
                        title="Clear all filters"
                      >
                        Clear
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                      onClick={() => { setCurrentPage(1); fetchProducts(); }}
                      disabled={loading}
                      title="Refresh table data"
                    >
                      {loading ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      ) : (
                        <RefreshCw size={14} />
                      )}
                      Refresh
                    </button>
                    <div className="d-flex align-items-center gap-2 ms-auto">
                      <button
                        ref={bulkAltButtonRef}
                        type="button"
                        className={`btn btn-sm ${selectedProducts.length > 0 ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={handleBulkAltText}
                        disabled={selectedProducts.length === 0}
                        title={selectedProducts.length === 0 ? 'Select one or more products to enable' : `Generate alt text for ${selectedProducts.length} selected product(s)`}
                      >
                        Bulk Generate Alt Text ({selectedProducts.length})
                      </button>
                      {/* <span className={`small ${selectedProducts.length > 0 ? '' : 'text-muted'}`}> */}
                        {/* {selectedProducts.length} selected */}
                      {/* </span> */}
                    </div>
                  </div>
                </div>
              </div>

              {altFilterScanCapped && (
                <div className="alert alert-warning py-2 px-3 mb-3 small" role="status">
                  {t("products.imagesAltFilterScanCapped")}
                </div>
              )}

              {/* Filter Summary */}
              {(selectedCategory || searchTerm || sortField || imagesAltFilter !== "all" || productListingFilter !== "all") && (
                <div className="mb-3">
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>Active filters:</small>
                    {imagesAltFilter === "missing_alt" && (
                      <span className="badge bg-warning text-dark">
                        {t("products.imagesAltFilterMissing")}
                        <button
                          type="button"
                          className="btn-close ms-2"
                          style={{ fontSize: "0.5rem" }}
                          aria-label={t("products.imagesAltFilterClear")}
                          onClick={() => { setImagesAltFilter("all"); setCurrentPage(1); }}
                        />
                      </span>
                    )}
                    {imagesAltFilter === "with_alt" && (
                      <span className="badge bg-success">
                        {t("products.imagesAltFilterWithAlt")}
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: "0.5rem" }}
                          aria-label={t("products.imagesAltFilterClear")}
                          onClick={() => { setImagesAltFilter("all"); setCurrentPage(1); }}
                        />
                      </span>
                    )}
                    {selectedCategory && (
                      <span className="badge bg-primary">
                        Category: {categories.find(c => c.id == selectedCategory)?.name || selectedCategory}
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '0.5rem' }}
                          onClick={() => setSelectedCategory("")}
                        ></button>
                      </span>
                    )}
                    {searchTerm && (
                      <span className="badge bg-info">
                        Search: "{searchTerm}"
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '0.5rem' }}
                          onClick={() => setSearchTerm("")}
                        ></button>
                      </span>
                    )}
                    {sortField && (
                      <span className="badge bg-secondary">
                        Sort: {sortField} ({sortDirection})
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '0.5rem' }}
                          onClick={() => { setSortField(""); setSortDirection("asc"); }}
                        ></button>
                      </span>
                    )}
                    {productListingFilter === "online" && (
                      <span className="badge bg-secondary">
                        {t("seoOptimizer.listingOnlineOnly")}
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '0.5rem' }}
                          aria-label={t("seoOptimizer.productListingFilter")}
                          onClick={() => { setProductListingFilter("all"); setCurrentPage(1); }}
                        />
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Products Table */}
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedProducts.length === sortedProducts.length && sortedProducts.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th
                        style={{ width: '45%', cursor: 'pointer' }}
                        onClick={() => onSort('name')}
                        className={sortField === 'name' ? 'table-active' : ''}
                      >
                        <div className="d-flex align-items-center">
                          Product name
                          {sortField === 'name' && (
                            <span className="ms-1">
                              {sortDirection === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        style={{ width: '15%', cursor: 'pointer' }}
                        onClick={() => onSort('price')}
                        className={sortField === 'price' ? 'table-active' : ''}
                      >
                        <div className="d-flex align-items-center">
                          Price
                          {sortField === 'price' && (
                            <span className="ms-1">
                              {sortDirection === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th
                        style={{ width: '15%', cursor: 'pointer' }}
                        onClick={() => onSort('created')}
                        className={sortField === 'created' ? 'table-active' : ''}
                      >
                        <div className="d-flex align-items-center">
                          Created Date
                          {sortField === 'created' && (
                            <span className="ms-1">
                              {sortDirection === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />}
                            </span>
                          )}
                        </div>
                      </th>
                      <th style={{ width: '10%' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedProducts.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-5">
                          <Package size={48} className="text-muted mb-3" />
                          <h5 className="text-muted" style={{ fontSize: '1rem' }}>No products found</h5>
                          <p className="text-muted" style={{ fontSize: '0.875rem' }}>Start by adding your first product to your store.</p>
                        </td>
                      </tr>
                    ) : (
                      sortedProducts.map(product => (
                        <tr key={product.id}>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedProducts.includes(product.id)}
                              onChange={() => handleSelectProduct(product.id)}
                            />
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <img
                                src={product.imageUrl || ""}
                                alt={product.name}
                                className="rounded me-3"
                                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                              />
                              <div>
                                <div className="fw-semibold">{product.name}</div>
                                <small className="text-muted" style={{ fontSize: '0.75rem' }}>SKU: {product.sku || "N/A"}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="fw-semibold">
                              ${(product.price || 0).toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <span className="text-muted">
                              {product.created ? new Date(product.created).toLocaleDateString() : 'N/A'}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-sm btn-outline-primary"
                                title="Generate Alt Text"
                                onClick={(e) => handleGenerateAltText(product.id, product.name, e)}
                                disabled={generatingAltText[product.id]}
                              >
                                {generatingAltText[product.id] ? (
                                  <span className="loading-dots" role="status" aria-hidden="true"></span>
                                ) : (
                                  <Wand2 size={16} />
                                )}
                              </button>
                              {/* <button
                                className="btn btn-sm btn-outline-secondary"
                                title="Edit Product"
                                onClick={() => handleEditProduct(product.id)}
                              >
                                <Edit size={16} />
                              </button> */}

                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination and Items Per Page */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center">
                  <label className="me-2" style={{ fontSize: '0.875rem' }}>Items per page:</label>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto' }}
                    value={itemsPerPage}
                    onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                  >
                    {itemsPerPageOptions.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    <li className="page-item">
                      <span className="page-link">
                        Page {currentPage} of {Math.ceil(totalProducts / itemsPerPage)}
                      </span>
                    </li>
                    <li className={`page-item ${currentPage >= Math.ceil(totalProducts / itemsPerPage) ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        disabled={currentPage >= Math.ceil(totalProducts / itemsPerPage)}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products; 